import { useEffect, useState } from "react";
import { fetchCapabilities } from "@repo/ui/chat";
import type { CapabilityCard, KyInstance } from "@repo/ui/chat";

interface CapabilitiesState {
  cards: CapabilityCard[];
  /** True from mount until the bootstrap resolves or fails. */
  loading: boolean;
  /** Set when the bootstrap failed; the welcome screen then shows its static cards. */
  error: string | undefined;
}

/**
 * Welcome-screen cards from the agent's `capabilities` bootstrap. Runs once per mount —
 * `App` holds this hook above `AgentProvider`, so a new chat remounts the provider but
 * not the bootstrap, and the registry only changes with an agent deploy.
 *
 * A failure is reported through `error` rather than thrown: the welcome screen falls back
 * to static cards, so the bootstrap degrades that screen rather than breaking it.
 */
export function useAgentCapabilities(api: KyInstance): CapabilitiesState {
  const [cards, setCards] = useState<CapabilityCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);

    fetchCapabilities(api)
      .then((fetched) => {
        if (cancelled) return;
        setCards(fetched);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setCards([]);
        setError(err instanceof Error ? err.message : "Failed to load agent capabilities");
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api]);

  return { cards, loading, error };
}
